/**
 * FeaturePermissions Component
 * 
 * Admin UI for managing feature-level access control.
 * Allows admins to configure which users/groups can access specific features.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Spinner,
  SpinnerSize,
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  Dropdown,
  IDropdownOption,
  TextField,
  Toggle,
  MessageBar,
  MessageBarType
} from '@fluentui/react';
import {
  LockClosedRegular,
  PeopleRegular,
  PersonRegular,
  AddRegular,
  DeleteRegular,
  SaveRegular,
  SearchRegular,
  CheckmarkCircleRegular,
  WarningRegular,
  SettingsRegular,
  AppGenericRegular,
  CloudRegular,
  BrainCircuitRegular,
  DatabaseRegular
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';

interface FeaturePermission {
  id: string;
  feature_id: string;
  feature_type: string;
  display_name: string;
  description: string;
  icon: string;
  access_mode: 'all_users' | 'specific_users';
  allowed_users: string[];
  allowed_groups: string[];
  created_at: string;
  updated_at: string;
}

interface EntraUser {
  id: string;
  displayName: string;
  email: string;
  userPrincipalName: string;
}

interface EntraGroup {
  id: string;
  displayName: string;
  description: string;
}

interface CacheInfo {
  cache_version: number;
  cache_valid: boolean;
  cache_ttl_seconds: number;
  cache_age_seconds: number | null;
  cached_items_count: number;
  all_permissions_cached: boolean;
}

const FeaturePermissions: React.FC = () => {
  const [permissions, setPermissions] = useState<FeaturePermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [refreshingCache, setRefreshingCache] = useState(false);
  const [cacheInfo, setCacheInfo] = useState<CacheInfo | null>(null);
  
  // Edit dialog state
  const [editingPermission, setEditingPermission] = useState<FeaturePermission | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // User/Group search
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [searchingGroups, setSearchingGroups] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState<EntraUser[]>([]);
  const [groupSearchResults, setGroupSearchResults] = useState<EntraGroup[]>([]);

  // Load permissions
  const loadPermissions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/admin/feature-permissions');
      const data = await response.json();
      
      if (data.success) {
        setPermissions(data.permissions || []);
        if (data.cache) {
          setCacheInfo(data.cache);
        }
      } else {
        setError(data.error || 'Failed to load permissions');
      }
    } catch (err) {
      setError('Failed to load feature permissions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Sync models from Model Management
  const syncModels = async () => {
    try {
      setSyncing(true);
      setError(null);
      
      const response = await authenticatedFetch('/api/admin/feature-permissions/models/sync', {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        const message = data.synced.length > 0 
          ? `Synced ${data.synced.length} new models: ${data.synced.join(', ')}`
          : 'All models are already synced';
        setSuccess(message);
        loadPermissions();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(data.error || 'Failed to sync models');
      }
    } catch (err) {
      setError('Failed to sync models');
      console.error(err);
    } finally {
      setSyncing(false);
    }
  };

  // Refresh cache manually
  const refreshCache = async () => {
    try {
      setRefreshingCache(true);
      setError(null);
      
      const response = await authenticatedFetch('/api/admin/feature-permissions/cache/refresh', {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Cache refreshed successfully');
        if (data.cache) {
          setCacheInfo(data.cache);
        }
        loadPermissions();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Failed to refresh cache');
      }
    } catch (err) {
      setError('Failed to refresh cache');
      console.error(err);
    } finally {
      setRefreshingCache(false);
    }
  };

  // Search users in Entra
  const searchUsers = async (query: string) => {
    if (query.length < 2) {
      setUserSearchResults([]);
      return;
    }
    
    try {
      setSearchingUsers(true);
      const response = await authenticatedFetch(`/api/admin/entra/users/search?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      
      if (data.success) {
        setUserSearchResults(data.users || []);
      }
    } catch (err) {
      console.error('User search failed:', err);
    } finally {
      setSearchingUsers(false);
    }
  };

  // Search groups in Entra
  const searchGroups = async (query: string) => {
    if (query.length < 2) {
      setGroupSearchResults([]);
      return;
    }
    
    try {
      setSearchingGroups(true);
      const response = await authenticatedFetch(`/api/admin/entra/groups/search?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      
      if (data.success) {
        setGroupSearchResults(data.groups || []);
      }
    } catch (err) {
      console.error('Group search failed:', err);
    } finally {
      setSearchingGroups(false);
    }
  };

  // Save permission changes
  const savePermission = async () => {
    if (!editingPermission) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const response = await authenticatedFetch(
        `/api/admin/feature-permissions/${encodeURIComponent(editingPermission.feature_id)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            display_name: editingPermission.display_name,
            description: editingPermission.description,
            access_mode: editingPermission.access_mode,
            allowed_users: editingPermission.allowed_users,
            allowed_groups: editingPermission.allowed_groups
          })
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Permission updated successfully!');
        setShowEditDialog(false);
        loadPermissions();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Failed to save permission');
      }
    } catch (err) {
      setError('Failed to save permission');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Add user to allowed list
  const addUser = (email: string) => {
    if (!editingPermission) return;
    if (editingPermission.allowed_users.includes(email)) return;
    
    setEditingPermission({
      ...editingPermission,
      allowed_users: [...editingPermission.allowed_users, email]
    });
    setUserSearchQuery('');
    setUserSearchResults([]);
  };

  // Remove user from allowed list
  const removeUser = (email: string) => {
    if (!editingPermission) return;
    
    setEditingPermission({
      ...editingPermission,
      allowed_users: editingPermission.allowed_users.filter(u => u !== email)
    });
  };

  // Add group to allowed list
  const addGroup = (groupId: string, displayName: string) => {
    if (!editingPermission) return;
    if (editingPermission.allowed_groups.includes(groupId)) return;
    
    setEditingPermission({
      ...editingPermission,
      allowed_groups: [...editingPermission.allowed_groups, groupId]
    });
    setGroupSearchQuery('');
    setGroupSearchResults([]);
  };

  // Remove group from allowed list
  const removeGroup = (groupId: string) => {
    if (!editingPermission) return;
    
    setEditingPermission({
      ...editingPermission,
      allowed_groups: editingPermission.allowed_groups.filter(g => g !== groupId)
    });
  };

  const featureTypeLabels: Record<string, string> = {
    platform_feature: 'Platform Features',
    model: 'AI Models',
    data_source: 'Data Sources'
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Spinner size={SpinnerSize.large} label="Loading feature permissions..." />
      </div>
    );
  }

  // Group permissions by type
  const groupedPermissions = permissions.reduce((acc, p) => {
    const type = p.feature_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(p);
    return acc;
  }, {} as Record<string, FeaturePermission[]>);

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '24px' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <LockClosedRegular style={{ fontSize: '28px', color: '#667eea' }} />
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>Feature Permissions</h2>
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>
              Control which users and groups can access specific platform features
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <DefaultButton
            text={refreshingCache ? "Refreshing..." : "Refresh Cache"}
            iconProps={{ iconName: 'Refresh' }}
            onClick={refreshCache}
            disabled={refreshingCache}
            title="Force refresh the permissions cache"
          />
          <DefaultButton
            text={syncing ? "Syncing..." : "Sync Models"}
            iconProps={{ iconName: 'Sync' }}
            onClick={syncModels}
            disabled={syncing}
            title="Import all models from Model Management into permissions"
          />
        </div>
      </div>

      {/* Cache info bar */}
      {cacheInfo && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '16px',
          padding: '8px 12px',
          marginBottom: '16px',
          backgroundColor: cacheInfo.cache_valid ? '#f0fdf4' : '#fef3c7',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#666'
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <strong>Cache:</strong> 
            {cacheInfo.cache_valid ? (
              <><CheckmarkCircleRegular style={{ color: '#16a34a', fontSize: 14 }} /> Valid</>
            ) : (
              <><WarningRegular style={{ color: '#d97706', fontSize: 14 }} /> Stale</>
            )}
          </span>
          <span>|</span>
          <span>Version: {cacheInfo.cache_version}</span>
          <span>|</span>
          <span>Items: {cacheInfo.cached_items_count}</span>
          {cacheInfo.cache_age_seconds !== null && (
            <>
              <span>|</span>
              <span>Age: {Math.floor(cacheInfo.cache_age_seconds / 60)}m {cacheInfo.cache_age_seconds % 60}s</span>
            </>
          )}
          <span>|</span>
          <span>TTL: {Math.floor(cacheInfo.cache_ttl_seconds / 60)} minutes</span>
        </div>
      )}

      {error && (
        <MessageBar 
          messageBarType={MessageBarType.error} 
          onDismiss={() => setError(null)}
          style={{ marginBottom: '16px' }}
        >
          {error}
        </MessageBar>
      )}

      {success && (
        <MessageBar 
          messageBarType={MessageBarType.success} 
          onDismiss={() => setSuccess(null)}
          style={{ marginBottom: '16px' }}
        >
          {success}
        </MessageBar>
      )}

      {/* Permissions by type */}
      {Object.entries(groupedPermissions).map(([type, perms]) => (
        <div key={type} style={{ marginBottom: '32px' }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: 600, 
            color: '#374151',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {featureTypeLabels[type] || type}
            <span style={{ 
              fontSize: '12px', 
              fontWeight: 400, 
              color: '#9ca3af',
              background: '#f3f4f6',
              padding: '2px 8px',
              borderRadius: '10px'
            }}>
              {perms.length}
            </span>
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '16px'
          }}>
            {perms.map(permission => (
              <div
                key={permission.feature_id}
                style={{
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}
                onClick={() => {
                  setEditingPermission(permission);
                  setShowEditDialog(true);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: 10, 
                    background: '#e3f2fd', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {permission.feature_type === 'model' ? (
                      <BrainCircuitRegular style={{ fontSize: 20, color: '#667eea' }} />
                    ) : permission.feature_type === 'data_source' ? (
                      <DatabaseRegular style={{ fontSize: 20, color: '#667eea' }} />
                    ) : (
                      <SettingsRegular style={{ fontSize: 20, color: '#667eea' }} />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '4px' }}>
                      {permission.display_name}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '12px' }}>
                      {permission.description || 'No description'}
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      padding: '6px 10px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}>
                      {permission.access_mode === 'all_users' ? (
                        <>
                          <PeopleRegular style={{ color: '#667eea', fontSize: 14 }} />
                          <span style={{ color: '#334155' }}>All Users</span>
                        </>
                      ) : (
                        <>
                          <PersonRegular style={{ color: '#667eea', fontSize: 14 }} />
                          <span style={{ color: '#334155' }}>
                            {permission.allowed_users.length} users, {permission.allowed_groups.length} groups
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Edit Dialog */}
      <Dialog
        hidden={!showEditDialog}
        onDismiss={() => setShowEditDialog(false)}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: editingPermission?.display_name || 'Edit Permission',
          subText: 'Configure who can access this feature'
        }}
        modalProps={{
          isBlocking: true,
          styles: { main: { maxWidth: '600px' } }
        }}
      >
        {editingPermission && (
          <div style={{ padding: '0 24px' }}>
            {/* Access Mode Toggle */}
            <div style={{ marginBottom: '24px' }}>
              <Toggle
                label="Access Mode"
                checked={editingPermission.access_mode === 'all_users'}
                onText="All Users - Everyone can access"
                offText="Specific Users - Restricted access"
                onChange={(_, checked) => setEditingPermission({
                  ...editingPermission,
                  access_mode: checked ? 'all_users' : 'specific_users'
                })}
              />
            </div>

            {/* User/Group selection (only when specific_users) */}
            {editingPermission.access_mode === 'specific_users' && (
              <>
                {/* Allowed Users */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ fontWeight: 600, fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                    Allowed Users
                  </label>
                  
                  {/* User Search */}
                  <div style={{ position: 'relative', marginBottom: '12px' }}>
                    <TextField
                      placeholder="Search users by name or email..."
                      value={userSearchQuery}
                      onChange={(_, value) => {
                        setUserSearchQuery(value || '');
                        searchUsers(value || '');
                      }}
                      iconProps={{ iconName: 'Search' }}
                    />
                    
                    {/* Search Results Dropdown */}
                    {userSearchResults.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        maxHeight: '200px',
                        overflowY: 'auto'
                      }}>
                        {userSearchResults.map(user => (
                          <div
                            key={user.id}
                            onClick={() => addUser(user.email)}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f3f4f6'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                          >
                            <div style={{ fontWeight: 500 }}>{user.displayName}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>{user.email}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Selected Users */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {editingPermission.allowed_users.map(email => (
                      <div
                        key={email}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 8px',
                          background: '#f1f5f9',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      >
                        <PersonRegular style={{ fontSize: '14px', color: '#667eea' }} />
                        {email}
                        <DeleteRegular
                          style={{ fontSize: '14px', cursor: 'pointer', color: '#64748b' }}
                          onClick={() => removeUser(email)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Allowed Groups */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ fontWeight: 600, fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                    Allowed Groups
                  </label>
                  
                  {/* Group Search */}
                  <div style={{ position: 'relative', marginBottom: '12px' }}>
                    <TextField
                      placeholder="Search groups by name..."
                      value={groupSearchQuery}
                      onChange={(_, value) => {
                        setGroupSearchQuery(value || '');
                        searchGroups(value || '');
                      }}
                      iconProps={{ iconName: 'Search' }}
                    />
                    
                    {/* Search Results Dropdown */}
                    {groupSearchResults.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        maxHeight: '200px',
                        overflowY: 'auto'
                      }}>
                        {groupSearchResults.map(group => (
                          <div
                            key={group.id}
                            onClick={() => addGroup(group.id, group.displayName)}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f3f4f6'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                          >
                            <div style={{ fontWeight: 500 }}>{group.displayName}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>{group.description || 'No description'}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Selected Groups */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {editingPermission.allowed_groups.map(groupId => (
                      <div
                        key={groupId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 8px',
                          background: '#f1f5f9',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      >
                        <PeopleRegular style={{ fontSize: '14px', color: '#667eea' }} />
                        {groupId}
                        <DeleteRegular
                          style={{ fontSize: '14px', cursor: 'pointer', color: '#64748b' }}
                          onClick={() => removeGroup(groupId)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        
        <DialogFooter>
          <PrimaryButton 
            text={saving ? 'Saving...' : 'Save Changes'} 
            onClick={savePermission}
            disabled={saving}
          />
          <DefaultButton 
            text="Cancel" 
            onClick={() => setShowEditDialog(false)}
            disabled={saving}
          />
        </DialogFooter>
      </Dialog>
    </div>
  );
};

export default FeaturePermissions;
